import { create } from "zustand";
import type { Region, View } from "@gooner/shared";
import { type ViewInput, api, apiErrorOf } from "../../lib/api";
import { isOnGrid, snapRect } from "../lib/regionGeometry";

/**
 * `dirty` is not cosmetic. It is how a save in flight finds out that something newer
 * landed while it was away — without it, a mutation is indistinguishable from no
 * mutation and the server's echo overwrites the edit on screen.
 */
export type SaveStatus = "idle" | "dirty" | "saving" | "saved" | "error";

interface ViewState {
  view: View | null;
  loading: boolean;
  loadError: string | null;
  saveStatus: SaveStatus;
  saveError: string | null;
  /** The failed save was a GoonLords Gold cap (ApiError.goldUpsell) — the editor forks
   *  to the upsell modal on this instead of string-matching the sentence. */
  saveErrorGold: boolean;

  /** False while the view is temporary — it has an id but no row and no owner. */
  isSaved: boolean;

  /**
   * True while a promotion POST is in flight.
   *
   * Deliberately not `saveStatus === "saving"`, which is shared with the debounced
   * autosave: a second promotion has to be refused for the duration of the FIRST
   * promotion specifically, and the button that starts one has to be disabled for
   * exactly that long and no longer. Keying either on the shared status disables
   * "Save to library" on every unrelated autosave round trip — worst on the slow links
   * where it matters most, since that is when the button is dead longest.
   */
  promoting: boolean;

  /**
   * The generation guard. Bumped by everything that changes what this store holds or
   * has in flight: every mutation, every load, every request going out, every identity
   * adopted from a promotion.
   *
   * A response may only be committed while this is still what it was when that request
   * left — meaning nothing at all has happened since. That is the one rule that holds
   * under arbitrary reordering, and it is why this is a counter rather than a flag.
   * `saveStatus === "saving"` cannot do the job: a SECOND save re-arms it, so the
   * first save's stale echo finds the door open again and commits content the server
   * has already superseded. With two overlapping autosaves and normal
   * first-out-first-back ordering, that makes the OLDER echo win — the store ends up
   * holding V1 while the server holds V2, marked "Saved", with nothing scheduled to
   * reconcile them, and the next edit builds on V1 and overwrites the server.
   *
   * Monotonic for the life of the store, never reset — `reset()` bumps it like
   * everything else. Restarting the count would let a request still in flight match a
   * generation minted after it.
   *
   * One exception, in `saveToLibrary`: a promotion whose response arrives after the
   * store was emptied (`reset()` on unmount, `remove()`) is committed anyway, because
   * the alternative is building a View by spreading `null`. It resurrects a view into a
   * store nobody is reading — inert while ViewEditor is the only consumer, since it
   * would re-load or short-circuit onto identical content, but it IS a hole in the rule
   * above and not something to build on. The strict form is to commit nothing when the
   * store has been emptied; it is left alone here only because changing promotion
   * behaviour was out of scope for the round that found it.
   */
  rev: number;

  load: (id: string) => Promise<void>;
  fetch: (id: string) => Promise<View | null>;
  list: () => Promise<View[]>;
  startTemp: (input: ViewInput) => Promise<void>;
  applyRegions: (regions: Region[]) => void;
  setAspect: (aspect: { w: number; h: number }) => void;
  setAdaptiveLayout: (adaptiveLayout: boolean) => void;
  rename: (name: string) => void;
  setIcon: (icon: string | undefined) => void;
  persist: () => Promise<void>;
  saveToLibrary: () => Promise<View | null>;
  remove: (id: string) => Promise<void>;
  reset: () => void;
}

const inputOf = (v: View): ViewInput => ({
  name: v.name,
  icon: v.icon,
  aspect: v.aspect,
  ...(v.adaptiveLayout ? { adaptiveLayout: true } : {}),
  regions: v.regions,
});

/**
 * The one view being edited.
 *
 * Mutations are local and immediate so dragging a region is never waiting on a
 * request; `persist()` is what pushes the current state to the server, which is also
 * what any screen showing this view is reading. A temporary view persists exactly the
 * same way — it has an id, it just has no row and no owner behind it.
 */
/**
 * Bring a view's regions onto the editing grid.
 *
 * Layouts built before the grid existed were dragged by eye and landed on values like
 * 0.37 — expressible, but not on any line the editor now draws. Loading one unchanged
 * would put every region a fraction off the gridlines behind it and make the first drag
 * look like a jump, when in truth the region had never been aligned at all.
 *
 * Presets are untouched by this: halves, thirds and quarters are all exact twelfths, so
 * every built-in layout is already on the grid and `isOnGrid` short-circuits.
 *
 * Returns the SAME object when nothing moves. The store's identity is read as "did the
 * view change" in several places, and a fresh object per load would mark an unmodified
 * view dirty and autosave it back for no reason.
 */
function onGrid(view: View): View {
  if (view.regions.every(isOnGrid)) return view;
  return {
    ...view,
    regions: view.regions.map((r) =>
      isOnGrid(r)
        ? r
        : { ...r, ...snapRect({ x: r.x, y: r.y, w: r.w, h: r.h }) },
    ),
  };
}

export const useViewStore = create<ViewState>((set, get) => ({
  view: null,
  loading: false,
  loadError: null,
  saveStatus: "idle",
  saveError: null,
  saveErrorGold: false,
  isSaved: false,
  promoting: false,
  rev: 0,

  load: async (id) => {
    // Bumping on the way IN is what protects the view being loaded: a save still in
    // flight for the PREVIOUS view is invalidated here, before its echo can land on
    // top of whatever arrives next.
    set((s) => ({ loading: true, loadError: null, rev: s.rev + 1 }));
    try {
      const { view } = await api.views.get(id);
      set((s) => ({
        view: onGrid(view),
        loading: false,
        isSaved: !id.startsWith("t_"),
        saveStatus: "idle",
        saveError: null,
        rev: s.rev + 1,
      }));
    } catch (e) {
      set((s) => ({
        loading: false,
        loadError: e instanceof Error ? e.message : String(e),
        view: null,
        rev: s.rev + 1,
      }));
    }
  },

  fetch: async (id) => {
    try {
      return (await api.views.get(id)).view;
    } catch {
      return null;
    }
  },

  list: async () => {
    try {
      return (await api.views.list()).views;
    } catch {
      return [];
    }
  },

  startTemp: async (input) => {
    set((s) => ({ loading: true, loadError: null, rev: s.rev + 1 }));
    try {
      const { view } = await api.views.createTemp(input);
      set((s) => ({
        view,
        loading: false,
        isSaved: false,
        saveStatus: "idle",
        saveError: null,
        rev: s.rev + 1,
      }));
    } catch (e) {
      set((s) => ({
        loading: false,
        loadError: e instanceof Error ? e.message : String(e),
        rev: s.rev + 1,
      }));
    }
  },

  // Every mutation is local, immediate, marked dirty and moves the generation on. The
  // dirty flag is for the save indicator, which would otherwise keep claiming "Saved"
  // over an edit that hasn't been sent yet; the generation is what any response still
  // in flight checks itself against.

  applyRegions: (regions) =>
    set((s) =>
      s.view
        ? { view: { ...s.view, regions }, saveStatus: "dirty", rev: s.rev + 1 }
        : {},
    ),

  setAspect: (aspect) =>
    set((s) =>
      s.view
        ? { view: { ...s.view, aspect }, saveStatus: "dirty", rev: s.rev + 1 }
        : {},
    ),

  setAdaptiveLayout: (adaptiveLayout) =>
    set((s) =>
      s.view
        ? { view: { ...s.view, adaptiveLayout }, saveStatus: "dirty", rev: s.rev + 1 }
        : {},
    ),

  rename: (name) =>
    set((s) =>
      s.view
        ? { view: { ...s.view, name }, saveStatus: "dirty", rev: s.rev + 1 }
        : {},
    ),

  setIcon: (icon) =>
    set((s) =>
      s.view
        ? { view: { ...s.view, icon }, saveStatus: "dirty", rev: s.rev + 1 }
        : {},
    ),

  persist: async () => {
    const view = get().view;
    if (!view) return;
    // Stamp the request with the generation it is sending, and move the generation on
    // so anything issued after it is distinguishable from it.
    const stamp = get().rev + 1;
    set({ rev: stamp, saveStatus: "saving", saveError: null, saveErrorGold: false });
    try {
      const { view: saved } = await api.views.update(view.id, inputOf(view), "autosave");
      // Still the newest thing that has happened? Then this echo describes the store
      // as it is, and taking it is free. Otherwise something — an edit, a second
      // autosave, a promotion, a load — has overtaken it, and this response describes
      // a past that must not be reinstated.
      set((s) =>
        s.rev === stamp
          ? { view: saved, saveStatus: "saved" as const, saveError: null }
          : {},
      );
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      // Only report a failure that is still the current state of affairs. If something
      // overtook this request, the store is dirty and the edit is still outstanding —
      // saying "error" there would erase the one flag the retry and the unmount flush
      // read, and the failure being reported isn't about what's on screen any more.
      set((s) =>
        s.rev === stamp
          ? {
              saveStatus: "error" as const,
              saveError: message,
              // A Gold cap refusing the AUTOSAVE must not die in the SavePill's
              // tooltip: the editor watches this flag and opens the upsell. It exists
              // for the stale-client case — the pre-empt gate reads settings cached at
              // page load, so a caps flip mid-session reaches this backstop first.
              saveErrorGold: apiErrorOf(e)?.goldUpsell === true,
            }
          : {},
      );
    }
  },

  saveToLibrary: async () => {
    const view = get().view;
    if (!view) return null;
    // Serialize. A second call while one is in flight POSTs again, and the server mints
    // a SECOND library row: the caller follows one response and the other row is
    // orphaned under the user's own name. Nothing else stops this — unlike the autosave
    // PUTs, which at least share a debounce. Set synchronously below, before the first
    // await, so a second call cannot get past this line while the first is away.
    //
    // This lives here rather than in the screen because the store is what mints the
    // row, and a guard in a component is unreachable from the checks.
    if (get().promoting) return null;
    const stamp = get().rev + 1;
    set({ rev: stamp, saveStatus: "saving", saveError: null, saveErrorGold: false, promoting: true });
    try {
      // Always promote from what's in memory, never by `{ from: id }`. That variant
      // makes the server copy its OWN last-persisted state, so with a debounced
      // persist() a rename followed immediately by Save would save the old name — the
      // edit on screen is the one the user means, and it's the one that must land. It
      // also still works when the temporary view has already expired.
      const { view: saved } = await api.views.save(inputOf(view));
      set((s) => {
        // The identity ALWAYS moves to the saved row. Unlike a persist echo this is
        // not a description of the past — the row exists and this store is editing it
        // now, and an editor left on the temporary id would autosave into a copy that
        // expires. Adopting it moves the generation on, which is precisely what stops
        // an autosave still in flight against the OLD id from landing afterwards and
        // dragging the store back onto it.
        const current = s.view;
        // `|| !current` accepts this response regardless of generation when the store
        // has been emptied under it — see the exception noted on `rev`. It is here to
        // avoid spreading `null` into a View, not because a reset store wants the
        // promotion back.
        const fresh = s.rev === stamp || !current;
        return fresh
          ? {
              view: saved,
              isSaved: true,
              saveStatus: "saved" as const,
              saveError: null,
              rev: s.rev + 1,
            }
          : {
              // Something on screen is newer than the body that was just saved. Keep
              // it, take only the identity, and report dirty — the caller has to send
              // again, which is why ViewEditor flushes after promoting. Clearing
              // saveError matters too: a concurrent persist may have failed and left
              // a message that no longer describes anything.
              view: {
                ...current,
                id: saved.id,
                ownerId: saved.ownerId,
                createdAt: saved.createdAt,
                updatedAt: saved.updatedAt,
              },
              isSaved: true,
              saveStatus: "dirty" as const,
              saveError: null,
              rev: s.rev + 1,
            };
      });
      return saved;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      // Not guarded on the GENERATION, unlike persist's: a user pressed a button and it
      // did not work, which is news whatever has been edited since. Guarding it that way
      // means an edit made during the request suppresses the whole report — no status,
      // no message, no navigation — and then the debounced autosave from that same edit
      // lands on the temporary row and paints "Kept for now", reading as success.
      //
      // Still guarded on IDENTITY, though. Correctness needed the generation dropped; it
      // never needed "is this still the view I was saving?" dropped too. Without this an
      // editor that has moved on — Save, navigate back, open another view, and only then
      // the POST fails — shows view A's failure against view B.
      set((s) =>
        s.view?.id === view.id
          ? { saveStatus: "error", saveError: message, saveErrorGold: apiErrorOf(e)?.goldUpsell === true }
          : {},
      );
      return null;
    } finally {
      // Every exit — saved, failed, or the store emptied under it — releases the lock.
      // A promotion that throws must not leave the button dead for the session.
      set({ promoting: false });
    }
  },

  remove: async (id) => {
    await api.views.remove(id).catch(() => undefined);
    set((s) =>
      s.view?.id === id ? { view: null, isSaved: false, rev: s.rev + 1 } : {},
    );
  },

  reset: () =>
    set((s) => ({
      view: null,
      loading: false,
      loadError: null,
      saveStatus: "idle",
      saveError: null,
      isSaved: false,
      // `promoting` is deliberately absent: the in-flight promotion's own `finally`
      // owns that flag, and clearing it here would unlock a second POST while the
      // first is still away — the exact thing the lock exists to prevent.
      // Bumped, never zeroed: a save still in flight has to fail its check on the way
      // back, and restarting the count would eventually hand it a matching generation.
      rev: s.rev + 1,
    })),
}));
